import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import rehypeHighlight from 'rehype-highlight';
import { getPostBySlug, loadBlogPost, formatDate, getRelatedPosts, type BlogPost as BlogPostType } from '../lib/blog';
import {
  Calendar, ArrowLeft, Share2, Linkedin, Twitter, Facebook, User, Tag,
  Snowflake, Sun, CheckSquare, Square, AlertTriangle, Lightbulb, Info,
  CheckCircle, XCircle, Star, Zap, Target, TrendingUp, Users, Building,
  FileText, Image, MessageSquare, Settings, Search, BarChart, Clock3,
  RefreshCw, Shield, Award, Bookmark, Flag, Heart, ThumbsUp, Eye,
  MapPin, Phone, Mail, Globe, Camera, Edit, Trash, Plus, Minus,
  ChevronRight, ArrowRight, ExternalLink, Download, Upload, Copy
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import BlogSectionHeader from '../components/BlogSectionHeader';
import ContactFormModal from '../components/ContactFormModal';
import { SEO, BASE_URL, createArticleJsonLd, createBreadcrumbJsonLd } from '../components/SEO';
import 'highlight.js/styles/github-dark.css';

// Icon mapping for blog content
const iconMap: Record<string, LucideIcon> = {
  'snowflake': Snowflake,
  'sun': Sun,
  'check-square': CheckSquare,
  'square': Square,
  'alert-triangle': AlertTriangle,
  'lightbulb': Lightbulb,
  'info': Info,
  'check-circle': CheckCircle,
  'x-circle': XCircle,
  'star': Star,
  'zap': Zap,
  'target': Target,
  'trending-up': TrendingUp,
  'users': Users,
  'building': Building,
  'file-text': FileText,
  'image': Image,
  'message-square': MessageSquare,
  'settings': Settings,
  'search': Search,
  'bar-chart': BarChart,
  'clock': Clock3,
  'refresh': RefreshCw,
  'shield': Shield,
  'award': Award,
  'bookmark': Bookmark,
  'flag': Flag,
  'heart': Heart,
  'thumbs-up': ThumbsUp,
  'eye': Eye,
  'map-pin': MapPin,
  'phone': Phone,
  'mail': Mail,
  'globe': Globe,
  'camera': Camera,
  'edit': Edit,
  'trash': Trash,
  'plus': Plus,
  'minus': Minus,
  'chevron-right': ChevronRight,
  'arrow-right': ArrowRight,
  'external-link': ExternalLink,
  'download': Download,
  'upload': Upload,
  'copy': Copy,
};

// Component to render icons from class names
function BlogIcon({ iconName, className }: { iconName: string; className?: string }) {
  const IconComponent = iconMap[iconName];
  if (!IconComponent) return null;
  return <IconComponent className={`inline-block w-5 h-5 ${className || ''}`} />;
}

export default function BlogPost() {
  const { slug } = useParams<{ slug: string }>();
  const [content, setContent] = useState<string>('');
  const [post, setPost] = useState<BlogPostType | null>(null);
  const [relatedPosts, setRelatedPosts] = useState<BlogPostType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);

  useEffect(() => {
    if (!slug) {
      setError('No blog post specified');
      setLoading(false);
      return;
    }

    const postData = getPostBySlug(slug);
    if (!postData) {
      setError('Blog post not found');
      setLoading(false);
      return;
    }

    setPost(postData);
    setRelatedPosts(getRelatedPosts(slug, 3));

    loadBlogPost(slug)
      .then(markdown => {
        // Remove the first h1 heading from markdown since we display the title in the article header
        const contentWithoutTitle = markdown.replace(/^#\s+.+\n+/, '');
        setContent(contentWithoutTitle);
        setLoading(false);
      })
      .catch(err => {
        console.error('Error loading blog post:', err);
        setError('Failed to load blog post');
        setLoading(false);
      });
  }, [slug]);

  const shareUrl = typeof window !== 'undefined' ? window.location.href : '';
  const canonicalUrl = `${BASE_URL}/blog/${slug}`;
  const shareTitle = post?.title || '';

  const handleShare = (platform: 'twitter' | 'linkedin' | 'facebook') => {
    let url = '';
    switch (platform) {
      case 'twitter':
        url = `https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareTitle)}`;
        break;
      case 'linkedin':
        url = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`;
        break;
      case 'facebook':
        url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`;
        break;
    }
    window.open(url, '_blank', 'width=600,height=400');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading article...</p>
        </div>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-4">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">Article Not Found</h1>
          <p className="text-gray-600 mb-8">{error || 'The article you\'re looking for doesn\'t exist.'}</p>
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Blog
          </Link>
        </div>
      </div>
    );
  }

  // Truncate title for breadcrumb display
  const truncatedTitle = post.title.length > 50
    ? post.title.substring(0, 50) + '...'
    : post.title;

  // Create JSON-LD structured data for article
  const articleJsonLd = createArticleJsonLd({
    title: post.title,
    description: post.description,
    url: canonicalUrl,
    datePublished: post.publishDate,
    author: post.author,
    section: post.category
  });

  // Create breadcrumb JSON-LD
  const breadcrumbJsonLd = createBreadcrumbJsonLd([
    { name: 'Home', url: BASE_URL },
    { name: 'Blog', url: `${BASE_URL}/blog` },
    { name: post.title, url: canonicalUrl }
  ]);

  return (
    <div className="min-h-screen bg-gray-100">
      <SEO
        title={post.title}
        description={post.description}
        canonical={canonicalUrl}
        type="article"
        article={{
          publishedTime: post.publishDate,
          author: post.author,
          section: post.category,
          tags: post.tags
        }}
        jsonLd={[articleJsonLd, breadcrumbJsonLd]}
      />
      {/* Blog Section Header with Breadcrumbs */}
      <BlogSectionHeader
        title="BrandIndex AI Blog"
        compact
        breadcrumbs={[
          { label: 'Home', href: '/' },
          { label: 'Blog', href: '/blog' },
          { label: truncatedTitle },
        ]}
      />

      {/* Main Content Area */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Article Header Card */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 sm:p-8 mb-6">
          {/* Category Badge */}
          <div className="mb-4">
            <span className="inline-block px-3 py-1 bg-indigo-100 text-indigo-700 text-sm font-medium rounded-full">
              {post.category}
            </span>
          </div>

          {/* Title */}
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
            {post.title}
          </h1>

          {/* Description */}
          <p className="text-lg text-gray-600 mb-6">
            {post.description}
          </p>

          {/* Meta Info Row */}
          <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500 pb-6 border-b border-gray-200">
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-indigo-500" />
              <time dateTime={post.publishDate}>{formatDate(post.publishDate)}</time>
            </div>
            <div className="flex items-center gap-2">
              <User className="w-4 h-4 text-indigo-500" />
              <span>{post.author}</span>
            </div>
          </div>

          {/* Tags and Share Row */}
          <div className="flex flex-wrap items-center justify-between gap-4 pt-4">
            {/* Tags */}
            {post.tags && post.tags.length > 0 && (
              <div className="flex items-center gap-2 flex-wrap">
                <Tag className="w-4 h-4 text-gray-400" />
                {post.tags.slice(0, 4).map((tag) => (
                  <span
                    key={tag}
                    className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-md"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            )}

            {/* Share Buttons */}
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-500 flex items-center gap-1">
                <Share2 className="w-4 h-4" />
                Share:
              </span>
              <button
                onClick={() => handleShare('twitter')}
                className="p-2 text-gray-500 hover:text-blue-400 hover:bg-blue-50 rounded-lg transition-colors"
                aria-label="Share on Twitter"
              >
                <Twitter className="w-4 h-4" />
              </button>
              <button
                onClick={() => handleShare('linkedin')}
                className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                aria-label="Share on LinkedIn"
              >
                <Linkedin className="w-4 h-4" />
              </button>
              <button
                onClick={() => handleShare('facebook')}
                className="p-2 text-gray-500 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-colors"
                aria-label="Share on Facebook"
              >
                <Facebook className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Article Content Card */}
        <article className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 sm:p-8 mb-6">
          <div className="prose prose-lg prose-indigo max-w-none
            prose-headings:scroll-mt-20
            prose-h2:text-2xl prose-h2:font-bold prose-h2:text-gray-900 prose-h2:border-b prose-h2:border-gray-200 prose-h2:pb-3 prose-h2:mb-6 prose-h2:mt-8 first:prose-h2:mt-0
            prose-h3:text-xl prose-h3:font-semibold prose-h3:text-gray-800 prose-h3:mt-6
            prose-h4:text-lg prose-h4:font-semibold prose-h4:text-gray-800 prose-h4:mt-4
            prose-p:text-gray-700 prose-p:leading-relaxed
            prose-li:text-gray-700
            prose-strong:text-gray-900
            prose-a:text-indigo-600 prose-a:no-underline hover:prose-a:underline
            prose-blockquote:border-l-indigo-500 prose-blockquote:bg-indigo-50 prose-blockquote:py-1 prose-blockquote:px-4 prose-blockquote:rounded-r-lg prose-blockquote:not-italic
            prose-code:bg-gray-100 prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded prose-code:text-sm prose-code:before:content-none prose-code:after:content-none prose-code:text-gray-800
            prose-pre:bg-gray-900 prose-pre:rounded-xl
            [&_pre_code]:bg-transparent [&_pre_code]:p-0 [&_pre_code]:text-inherit
            prose-hr:border-gray-200 prose-hr:my-8
            prose-table:border prose-table:border-gray-200 prose-table:rounded-lg prose-table:overflow-hidden
            prose-th:bg-gray-50 prose-th:px-4 prose-th:py-3 prose-th:text-left prose-th:font-semibold prose-th:text-gray-900 prose-th:border-b prose-th:border-gray-200
            prose-td:px-4 prose-td:py-3 prose-td:border-b prose-td:border-gray-100
            prose-img:rounded-xl prose-img:shadow-md

            /* Custom card styles for visual separation */
            [&_.card]:bg-gray-50 [&_.card]:rounded-xl [&_.card]:p-5 [&_.card]:my-6 [&_.card]:border [&_.card]:border-gray-200
            [&_.card-indigo]:bg-indigo-50 [&_.card-indigo]:rounded-xl [&_.card-indigo]:p-5 [&_.card-indigo]:my-6 [&_.card-indigo]:border [&_.card-indigo]:border-indigo-200
            [&_.card-green]:bg-emerald-50 [&_.card-green]:rounded-xl [&_.card-green]:p-5 [&_.card-green]:my-6 [&_.card-green]:border [&_.card-green]:border-emerald-200
            [&_.card-amber]:bg-amber-50 [&_.card-amber]:rounded-xl [&_.card-amber]:p-5 [&_.card-amber]:my-6 [&_.card-amber]:border [&_.card-amber]:border-amber-200
            [&_.card-blue]:bg-sky-50 [&_.card-blue]:rounded-xl [&_.card-blue]:p-5 [&_.card-blue]:border [&_.card-blue]:border-sky-200
            [&_.card-orange]:bg-orange-50 [&_.card-orange]:rounded-xl [&_.card-orange]:p-5 [&_.card-orange]:border [&_.card-orange]:border-orange-200
            [&_.card-purple]:bg-purple-50 [&_.card-purple]:rounded-xl [&_.card-purple]:p-5 [&_.card-purple]:border [&_.card-purple]:border-purple-200
            [&_.card-pink]:bg-pink-50 [&_.card-pink]:rounded-xl [&_.card-pink]:p-5 [&_.card-pink]:border [&_.card-pink]:border-pink-200
            [&_.card-teal]:bg-teal-50 [&_.card-teal]:rounded-xl [&_.card-teal]:p-5 [&_.card-teal]:border [&_.card-teal]:border-teal-200

            /* Grid layouts for columns */
            [&_.grid-2]:grid [&_.grid-2]:grid-cols-1 [&_.grid-2]:md:grid-cols-2 [&_.grid-2]:gap-4 [&_.grid-2]:my-6
            [&_.grid-3]:grid [&_.grid-3]:grid-cols-1 [&_.grid-3]:md:grid-cols-3 [&_.grid-3]:gap-4 [&_.grid-3]:my-6

            /* Grid items with card styling */
            [&_.grid-item]:bg-gray-50 [&_.grid-item]:rounded-xl [&_.grid-item]:p-4 [&_.grid-item]:border [&_.grid-item]:border-gray-200
            [&_.grid-item]:flex [&_.grid-item]:flex-col
            [&_.grid-item_strong]:text-gray-900 [&_.grid-item_strong]:font-semibold [&_.grid-item_strong]:mb-2 [&_.grid-item_strong]:block
            [&_.grid-item_p]:text-sm [&_.grid-item_p]:text-gray-600 [&_.grid-item_p]:m-0
            [&_.grid-item_ul]:text-sm [&_.grid-item_ul]:text-gray-600 [&_.grid-item_ul]:m-0 [&_.grid-item_ul]:mt-2 [&_.grid-item_ul]:pl-4

            /* Callout/tip boxes */
            [&_.tip]:bg-blue-50 [&_.tip]:rounded-xl [&_.tip]:p-5 [&_.tip]:my-6 [&_.tip]:border-l-4 [&_.tip]:border-l-blue-500
            [&_.warning]:bg-amber-50 [&_.warning]:rounded-xl [&_.warning]:p-5 [&_.warning]:my-6 [&_.warning]:border-l-4 [&_.warning]:border-l-amber-500
            [&_.success]:bg-emerald-50 [&_.success]:rounded-xl [&_.success]:p-5 [&_.success]:my-6 [&_.success]:border-l-4 [&_.success]:border-l-emerald-500

            /* Example box */
            [&_.example]:bg-slate-50 [&_.example]:rounded-xl [&_.example]:p-5 [&_.example]:my-6 [&_.example]:border [&_.example]:border-slate-200
            [&_.example]:before:content-['Example'] [&_.example]:before:text-xs [&_.example]:before:font-semibold [&_.example]:before:text-slate-500 [&_.example]:before:uppercase [&_.example]:before:tracking-wider [&_.example]:before:block [&_.example]:before:mb-3

            /* Stats/metrics row - matches MetricCard component design */
            [&_.stats]:grid [&_.stats]:grid-cols-1 [&_.stats]:sm:grid-cols-2 [&_.stats]:lg:grid-cols-3 [&_.stats]:gap-4 [&_.stats]:my-8
            [&_.stat]:bg-white [&_.stat]:rounded-xl [&_.stat]:border [&_.stat]:border-gray-200 [&_.stat]:p-6 [&_.stat]:text-center [&_.stat]:flex [&_.stat]:flex-col [&_.stat]:items-center [&_.stat]:justify-center
            [&_.stat-value]:text-3xl [&_.stat-value]:font-bold [&_.stat-value]:text-gray-900 [&_.stat-value]:tracking-tight
            [&_.stat-label]:text-sm [&_.stat-label]:text-gray-600 [&_.stat-label]:mt-2 [&_.stat-label]:font-medium

            /* Feature list with icons */
            [&_.feature-list]:list-none [&_.feature-list]:pl-0 [&_.feature-list]:my-6 [&_.feature-list]:space-y-3
            [&_.feature-list_li]:flex [&_.feature-list_li]:items-start [&_.feature-list_li]:gap-3 [&_.feature-list_li]:bg-gray-50 [&_.feature-list_li]:rounded-lg [&_.feature-list_li]:p-3 [&_.feature-list_li]:border [&_.feature-list_li]:border-gray-200
          ">
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              rehypePlugins={[rehypeRaw, rehypeHighlight]}
              components={{
                // Custom rendering for links to open in new tab
                a: ({ node, ...props }) => (
                  <a {...props} target="_blank" rel="noopener noreferrer" />
                ),
                // Custom rendering for horizontal rules as section dividers
                hr: () => (
                  <div className="my-10 flex items-center gap-4">
                    <div className="flex-1 h-px bg-gradient-to-r from-transparent via-gray-300 to-transparent"></div>
                  </div>
                ),
                // Custom rendering for blockquotes
                blockquote: ({ node, children, ...props }) => (
                  <blockquote {...props} className="border-l-4 border-indigo-500 bg-indigo-50 py-3 px-5 rounded-r-lg my-6 not-italic">
                    {children}
                  </blockquote>
                ),
                // Custom rendering for spans with icon classes
                span: ({ node, className, children, ...props }) => {
                  // Check if this span has an icon class
                  if (className && className.startsWith('icon-')) {
                    const iconName = className.replace('icon-', '');
                    const colorClass = (props as any)['data-color'] || 'text-current';
                    return <BlogIcon iconName={iconName} className={colorClass} />;
                  }
                  return <span className={className} {...props}>{children}</span>;
                },
              }}
            >
              {content}
            </ReactMarkdown>
          </div>
        </article>

        {/* Related Posts Card */}
        {relatedPosts.length > 0 && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 sm:p-8 mb-6">
            <h3 className="text-xl font-bold text-gray-900 mb-6">Related Articles</h3>
            <div className="grid md:grid-cols-3 gap-4">
              {relatedPosts.map((relatedPost) => (
                <Link
                  key={relatedPost.slug}
                  to={`/blog/${relatedPost.slug}`}
                  className="group p-4 rounded-xl border border-gray-200 hover:border-indigo-400 hover:shadow-md transition-all bg-gray-50 hover:bg-white"
                >
                  <span className="inline-block px-2 py-0.5 bg-indigo-100 text-indigo-700 text-xs font-medium rounded mb-2">
                    {relatedPost.category}
                  </span>
                  <h4 className="font-semibold text-gray-900 group-hover:text-indigo-600 mb-2 line-clamp-2 text-sm">
                    {relatedPost.title}
                  </h4>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* CTA Card */}
        <div className="bg-white rounded-2xl shadow-sm border-2 border-indigo-200 p-6 sm:p-8">
          <div className="text-center max-w-2xl mx-auto">
            <div className="inline-flex items-center justify-center w-12 h-12 bg-indigo-100 rounded-full mb-4">
              <svg className="w-6 h-6 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-3">
              Ready to Track Your AI Visibility?
            </h3>
            <p className="text-gray-600 mb-6">
              BrandIndex AI helps local businesses track, measure, and improve their visibility
              across ChatGPT, Claude, and Gemini. Start your free 7-day trial today.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link
                to="/register"
                className="px-6 py-2.5 bg-indigo-600 text-white font-semibold rounded-lg hover:bg-indigo-700 transition-colors"
              >
                Start Free Trial
              </Link>
              <button
                onClick={() => setIsContactModalOpen(true)}
                className="px-6 py-2.5 bg-white text-indigo-600 font-semibold rounded-lg border-2 border-indigo-200 hover:border-indigo-300 hover:bg-indigo-50 transition-colors"
              >
                Book a Demo
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Contact Form Modal */}
      <ContactFormModal
        isOpen={isContactModalOpen}
        onClose={() => setIsContactModalOpen(false)}
      />
    </div>
  );
}
